/**
 * @Author: v_jppjpeng
 * @Date: 2022-10-24 15:37:28
 * @Explain: 定义类型
 */

import type { InputSizeType } from '@/components/tdesign/data.d';
import type { ReducersEnum } from '@/models/connect.d';
import type { Dispatch, ReactNode } from 'react';
import type {
  FormItemProps,
  InputProps,
  SelectProps,
  TextareaProps,
  TooltipProps,
} from 'tdesign-react';
import type { TdFormRule, TextThemeType } from './tdesign.d';

/**  语言 - 类型 */
export type LanguageType = 'zh-CN' | 'en-US' | 'ja-JP';
/**  语言 - 枚举 */
export enum LanguageEnum {
  ZH = 'zh-CN',
  EN = 'en-US',
  JA = 'ja-JP',
  KO = 'ko-KR',
}

/**  简写语言 - 类型 */
export type AbbreviationLanguageType = 'zh' | 'en' | 'ja' | 'ko';
/**  简写语言 - 枚举 */
export enum AbbreviationLanguageEnum {
  ZH = 'zh',
  EN = 'en',
  JA = 'ja',
  KO = 'ko',
}

export interface AppPropsType {
  dispatch: ({
    type,
    payload,
  }: {
    type: ReducersEnum;
    payload?: any;
  }) => Dispatch<any>;
}

/** 时间格式选项 */
export interface TimeFormatOptionsProps {
  label: string;
  value: string;
  /** 按照12小时制展示 */
  showFormatAmPm: string;
  /** 展示年月日时分格式 */
  showFormatAll: string;
  /** 展示年月日格式 */
  showFormatYMD: string;
  /** 展示年月日时分格式 */
  showFormatYMDHM: string;
  /** 组件格式 */
  componentFormat: string;
  /** 接口格式 YYYY-MM-DD HH:mm:ss */
  interfaceFormat: string;
  /** 接口格式 YYYY-MM-DDTHH:mm:ss */
  interfaceFormatNew: string;
  /** 展示月日格式 */
  showFormatMD: string;
  /** 展示时分格式 */
  showFormatHm: string;
}

// 以前基于tea的下拉的类型声明
export interface SelectItemType {
  text: any;
  value: any;
}

// 状态声明
export type StatusTipType =
  | 'init'
  | 'empty'
  | 'noMore'
  | 'searchEmpty'
  | 'loading'
  | 'found'
  | 'error'
  | 'success';

export enum StatusTipEnum {
  init = 'init',
  empty = 'empty',
  noMore = 'noMore',
  searchEmpty = 'searchEmpty',
  loading = 'loading',
  found = 'found',
  error = 'error',
  success = 'success',
}

// 链接上的参数 - action
export enum ActionTypeEnum {
  ADD = 'add',
  EDIT = 'edit',
  DETAILS = 'details',
  PREVIEW = 'preview',
  COPY = 'copy',
  SUSPEND = 'suspend',
  REVIEWS_ERROR = 'reviewsError',
}

// 操作类型枚举
export enum OperationEnum {
  /** 新增 */
  ADD = 'add',
  /** 编辑 */
  EDIT = 'edit',
  /** 删除 */
  DEL = 'delete',
  /** 提交 */
  SUBMIT = 'SUBMIT',
  /** 拖拽 */
  DRAG = 'DRAG',
}

// 布尔值类型
export enum BooleanTypeEnum {
  Y = 'Y',
  N = 'N',
}

// 输入框类型声明
export type InputType =
  | 'custom'
  | 'text'
  | 'hiddenInput'
  | 'input'
  | 'textarea'
  | 'select'
  | 'selectGroup'
  | 'checkboxGroup'
  | 'radioGroup'
  | 'datePicker'
  | string;

export enum InputEnum {
  custom = 'custom',
  text = 'text',
  hiddenInput = 'hiddenInput',
  input = 'input',
  textarea = 'textarea',
  select = 'select',
  selectGroup = 'selectGroup',
  checkboxGroup = 'checkboxGroup',
  radioGroup = 'radioGroup',
  datePicker = 'datePicker',
  switch = 'switch',
}

// 公用配置类型
export type CommonOptionsType = {
  label?: string | ReactNode;
  labelKey?: string;
  tips?: string;
  tipsKey?: string;
  /** 原生悬浮标题 */
  dataTitle?: string;
  dataTitleKey?: string;
  placeholder?: string;
  placeholderKey?: string;
  value: any;
  /** 是否展示 */
  isShow?: boolean;
  /** 开放展示的角色 */
  openRoles?: any[];
  disabled?: boolean;
  theme?: TextThemeType;
  config?: any;
  style?: CSSProperties;
  tooltipProps?: TooltipProps;
  /** 分组信息 */
  group?: string | number;
  onClick?: (value: any, item: CommonOptionsType) => void;
};

// 级联组件公用配置类型
export type CascaderCommonOptionsType = {
  children?: CascaderCommonOptionsType[];
} & CommonOptionsType;

// 简易表单 - 映射类型声明
export type PromptMappingTableType = {
  /** 输入框类型 */
  inputType: InputType;
  /** 字段 */
  field: string;
  label?: string;
  /** 标题 - key */
  labelKey?: string;
  /** 提示语 - key */
  msgKey?: string;
  /** 正则 */
  pattern?: RegExp;
  /** 验证规则 */
  validationRules?: { reg: RegExp; msgKey: string }[];
  className?: string;
  align?: string;
  /** 是否必填 */
  required?: boolean;
  /** 是否显示 */
  isShow?: boolean;
  /** 选项配置 */
  options?: any[];
  /** 内容props - 例如设置input的maxlength */
  contentProps?: any;
  onChange?: (
    val: any,
    contxt?: { onClearError?: (val: string) => void },
  ) => void;
  /** 自定义渲染 */
  onRender?: () => any;
  /** 装饰 */
  onAdornment?: (val: any) => any;
};

// tdesign - 简易表单
export type TdPromptMappingTableType = {
  /** 是否展示 */
  isShow?: boolean;
  /** 表单item展示类型  */
  inputType: InputEnum;
  /** 键名 */
  field: string;
  /** 初始值 */
  initialData?: any;
  /** 表单item展示名称 */
  label?: string;
  labelKey?: string;
  /** 表单item展示placeholder */
  placeholder?: string;
  /** 表单item展示placeholder, 为intl的key */
  placeholderKey?: string;
  /** 表单item展示help */
  help?: string | ReactNode;
  /** 表单item展示help, 为intl的key */
  helpKey?: string;
  /** 表单-select-下拉选择 */
  options?: CommonOptionsType[];
  /** form-item - props */
  formItemProps?: FormItemProps;
  /** input, select - props */
  contentProps?: InputProps &
    TextareaProps &
    SelectProps & { size?: InputSizeType };
  /** 表单校验规则 */
  formRules?: TdFormRule[];
  /** 是否必填 */
  // required?: boolean;
  /** form-item 内容自定义渲染 */
  render?: (val: any, details: any) => ReactNode;
  /** form内 循环自定义渲染 */
  formItemRender?: () => ReactNode;
  /** 装饰器 */
  onAdornment?: (el: any) => ReactNode;
};

/** 需要完成的完成配置
 *
 * 1.发送地址
 * 2.发件人
 * 3.测试邮箱
 * 4.域名
 * 5.联系人
 * 6.标签
 * 7.分层
 * 8.模板
 * 9.表单
 * 10.webhook
 */
export enum CompleteConfigEnum {
  /** 是否配置发件地址 */
  CommonSendingAddress = 'CommonSendingAddress',
  /** 是否配置发件人 */
  CommonFromName = 'CommonFromName',
  /** 是否配置测试人员列表 */
  CampaignTest = 'CampaignTest',
  /** 是否配置域名管理 */
  Domain = 'Domain',
  /** 是否配置联系人 */
  IsBusinessUser = 'IsBusinessUser',
  /** 是否配置标签 */
  IsTag = 'IsTag',
  /** 是否配置分层 */
  IsFilter = 'IsFilter',
  /** 是否配置模板 */
  IsTemplate = 'IsTemplate',
  /** 是否配置注册表单 */
  HasSignupForm = 'HasSignupForm',
  /** 是否配置webhook */
  IsWebhook = 'IsWebhook',
}

/** 未知 - 标识符 */
export enum UnknownIdeEnum {
  /** 未知地点 */
  UNKNOWN_COUNTRY = '999',
  /** 未知语言 */
  UNKNOWN_LANGUAGE = 'un-Know',
}

/** 本地存储 - key */
export enum StoreKeyEnum {
  /** WhatsApp模板选择设置 */
  WHATSAPP_TEMPLATE_SELECT_ID = 'whatsAppTemplateAccountId',
  /** 区域管理-是否展开列表 */
  AREA_MANAGE_EXPANDED_GROUPS = 'areaManageExpandedGroups',
  /** 版本 */
  VERSIONS = 'versions',
  /** 回调地址 */
  RETURN_URL = 'returnUrl',
  /** 语言设置，例如zh-CN */
  LOCAL_LANGUAGE = 'localLanguage',
  /** 语言设置，例如zh */
  DESIGN_LANGUAGE = 'designLanguage',
  /** 当前本地时区 */
  LOCAL_TIMEZONE = 'localTimezone',
  /** 时间格式 类型 */
  TIME_FORMAT_TYPE = 'timeFormatType',
  /** token */
  ACCESS_TOKEN = 'accessToken',
  /** refresh token */
  REFRESH_TOKEN = 'refershToken',

  /** 邀请信息 */
  PROJECT_APPLY_INFO = 'projectApplyInfo',
  /** 登录 - 公告 */
  LOGIN_ANNOUNCEMENT_FLAG = 'loginAnnouncementFlag',
  /** 登录类型 */
  LOGIN_TYPE = 'loginType',
  /** 用户信息 */
  USER_INFO = 'userInfo',

  /** 列表 - 筛选条件(页长) */
  FILTER_LIST = 'filterList',
  /** 是否锁左侧菜单 */
  LEFT_MENU_LOCK = 'leftMenuLock',
  /** 左侧菜单是否折叠  */
  LEFT_MENU_COLLAPSE = 'leftMenuCollapse',
  /** 当前业务信息 */
  CURRENT_BUSINESS = 'currentBusiness0515',

  /** HOME - todolist用户状态相关信息 */
  HOME_TODOLIST_STATUS_INFO = 'toDoStatusInfo',

  /** 联系人上传文件 - 邮件域名 */
  EMAIL_DOMAIN = 'emailDomain',
  /** 联系人 - 预约设置信息 */
  NEWSLETTER_DETAILS = 'newsletterDetails',
  /** 联系人 - 预约设置信息 */
  NEWSLETTER_IS_DEAFT = 'newsletterIsDraft',

  /** 标签管理 - 提示信息展开状态 */
  LABEL_MANA_PREDICTIVE_ALERT = 'labelManaPredictiveAlert',

  /** 活动报表 - 报表详情信息 */
  REPORT_DETAIL_DATA = 'reportDetailData',
  /** 搜索信息 */
  REPORT_SEARCH_PARAMS = 'reportSearchParams',
  /** 触发初始化接口请求 */
  REPORT_IS_SEARCH = 'reportIsSearch',
  /** 记录其它途径跳转至此的业务ID */
  GAME_ID = 'gameId',
  /** 拒绝列表 - 上传文件CSV - 邮件域名 */
  BLOCK_LIST_EMAIL_DOMAIN = 'blockListEmailDomain',
  /** 营销邮件抽屉是否打开 */
  IS_MARKETING_DRAWER_OPENED = 'isMarketingDrawerOpened',
  /** 事务邮件抽屉是否打开 */
  IS_TRANS_DRAWER_OPENED = 'isTransDrawerOpened',
  /** 模板编辑器页面 是否点击过 主题 **/
  IS_CLICK_ADD_SUBJECT_BTN = 'isClickAddSubjectBtn',
  /** 缓存多语言表单所在页面内容 */
  CACHE_MAIL_MARKETING_FORM = 'savedMailTemplate',
  /** Discord 授权页 - 短链与渠道信息 */
  DISCORD_AUTHORIZE_QUERY_INFO = 'discordAuthorizeQueryInfo',
}

/** 前端环境 - gcp/gcp-studio */
export enum FrontEnvKeyEnum {
  FrontEnvKeyEnum_GCP = 'gcp',
  FrontEnvKeyEnum_GCP_STUDIO = 'gcp-studio',
}

// 行为上报 - 行为所属模块
export enum ModuleEnum {
  ACCOUNTS = 1,
  CAMPAIGNS = 2,
  CONTACTS = 3,
  SEGEMENTS = 4,
  Tags = 5,
  PREFERENCES = 6,
  EMAIL_TEMPLATES = 7,
  PLACEHOLDERS = 8,
  REPORTS = 9,
  //项目设置
  PROJECT_SETTINGS = 10,
}

// 行为上报 - 行为ID
export enum BehaviorEnum {
  CREATE_CAMPAIGN = 1,
  SIGN_OUT = 2,
  DELETE_CAMPAIGN = 3,
  EDIT_CAMPAIGN = 4,
  VIEW_CAMPAIGN = 5,
  VIEW_CAMPAIGN_REPORTS = 6,
  APPROVAL_OPERATIONS = 7,
  CREATE_CONTACT = 8,
  FILE_UPLOAD_CONTACT = 9,
  SEARCH_CONTACT_PERSON = 10,
  DELETE_CONTACT = 11,
  CREATE_SEGMENT_RULES = 12,
  COPY_SEGMENT_RULES = 13,
  QUERY_SEGMENT_RULES = 14,
  DELETE_SEGMENTATION_RULES = 15,
  SEND_SEGMENT_EVENT = 16,
  CREATE_TAG = 17,
  RENAME_TAG = 18,
  DELETE_TAG = 19,
  SEND_TAG_CAMPAIGN = 20,
  CREATE_PREFERENCES = 21,
  EDITORIAL_PREFERENCES = 22,
  DELETE_PREFERENCE = 23,
  CREATE_TEMPLATE = 24,
  EDIT_TEMPLATE = 25,
  DELETE_TEMPLATE = 26,
  COPY_TEMPLATE = 27,
  SEND_TEST_EMAIL = 28,
  EXPORT_HTML = 29,
  VIEW_PLACEHOLDERS = 30,
  CREATE_PLACEHOLDER = 31,
  EDIT_PLACEHOLDER = 32,
  REMOVE_PLACEHOLDER = 33,
  VIEW_REPORT = 34,
  INVITE_USERS = 35,
  EDIT_USER_ROLE = 36,
  DELETE_USER_ROLE = 37,
  ADD_DOMAIN_NAME = 38,
  DOMAIN_NAME_CERTIFICATION = 39,
  ADD_A_NEW_SENDER_ADDRESS = 40,
  EDIT_SENDER_ADDRESS = 41,
  DELETE_SENDER_ADDRESS = 42,
  ADD_SENDER_NAME = 43,
  EDIT_SENDER_NAME = 44,
  REMOVE_SENDER_NAME = 45,
  ADD_TESTER = 46,
  EDIT_TESTER = 47,
  DELETE_TESTER = 48,
  COPY_APIKEY = 49,
  VIEW_BLOCKLIST = 50,
  ADD_BLOCKLIST = 51,
  LOGIN = 52,
  COPY_CAMPAIGN = 53,
  VIEW_CAMPAIGN_ALL = 54,
  VIEW_CAMPAIGN_DRAFT = 55,
  VIEW_CAMPAIGN_ONGOING = 56,
  VIEW_CAMPAIGN_COMPLETE = 57,
  VIEW_CAMPAIGN_PENDING_APPROVAL = 58,
  VIEW_CAMPAIGN_APPROVA_LLIST = 59,
  BATCH_DELETE_CONTACTS = 60,
  SEARCH_SENDER_ADDRESS = 61,
  SEARCH_SENDER_NAME = 62,
  SEARCH_API_KEY = 63,
  ADD_API_KEY = 64,
  DELETE_DOMAIN_NAME = 65,
  SEARCH_DOMAIN_NAME = 66,
  SEARCH_EMAIL_TEMPLATE = 67,
  EDIT_API_KEY = 68,
  REVOKE_API_KEY = 69,
  STOP_CAMPAIGN = 70,
  // 设置中文
  'SET_CH_LANG' = 123,
  // 设置英文
  'SET_EN_LANG' = 124,
}
